import type { ComplianceUserMetric } from 'api/users/compliance.api';
import type { ItemStatusWrapper } from 'components/users/flexlist/FlexList';
import type { HoursPerWeekData } from 'components/users/HoursLoggedPerWeekGraph';
import type { TeamWithUsers } from 'factories/teams';
import { getIsDemoManagerPresetActive } from 'redux/modules/tic/tic.demo.selectors';
import {
  buildActivitiesDemoData,
  buildComplianceDemoUsers,
  buildDemoEnrichedUser,
  buildDemoTeamsForUser,
  buildDetailDashboardTotals,
  buildDetailReportChart,
  buildDetailReportProjects,
  buildDetailReportTags,
  buildDetailReportTotals,
  buildDetailTimesheetRows,
  buildInvitedListDemoUsers,
  buildListDemoTotals,
  buildListDemoUsers,
  buildPeopleDemoData,
  buildTeamsWithUsers,
  DEMO_DETAIL_USER_IDS,
  DEMO_INVITED_PEOPLE_COUNT,
  DEMO_INVITED_USER_IDS,
  DEMO_PEOPLE_COUNT,
  type DemoTimesheetRow,
} from 'redux/modules/users-page/demo/usersPage.demo.generator';
import type {
  UserViewModel,
  WeekSummary,
} from 'redux/modules/users-page/users-dashboard/usersDashboard.types';
import type { ListUser } from 'redux/modules/users-page/users-list/usersList.types';
import type { XhrStatusUIState } from 'redux/reusable/xhrStatus';
import { getStartOfWeek } from 'redux/selectors/accounts';
import { getCurrentlyViewedUserId } from 'redux/selectors/routing';
import { getTeamsWithUsers } from 'redux/selectors/teams';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { Activity } from 'types/activity';
import type { DurationSummary } from 'types/summaries';
import type { Team } from 'types/team';
import type { EnrichedUser } from 'types/user';
import { getLastWeeksRange } from 'utils/date-helper';
import { durationFromHours } from 'utils/durations';
import { type CostAndDurationSummary, formatDayRange } from 'utils/summary';

/**
 * Fictional People-page data for the Manager demo preset.
 *
 * Every selector here returns `undefined` unless the demo preset is active, so a
 * real account never pays for building fixtures. The People selectors pick the
 * demo value with `demo ?? real`, which means the real selector's own result
 * object is returned unchanged for everyone else — downstream memoization and
 * component re-render behaviour are identical to having no demo mode at all.
 *
 * This module deliberately reads the raw store rather than importing the
 * People selectors it feeds: those modules import this one, and a cycle between
 * them would leave `createSelector` inputs uninitialised at module evaluation.
 * Only leaf selector modules that import nothing from the People or user pages
 * — `accounts` and `routing` — are safe to depend on here.
 */

const readDashboardFilter = (state: IState) => state.usersPage.dashboard.filter;
const readListQuery = (state: IState) => state.usersPage.list.query;

const EMPTY_IDS: number[] = [];

/** A settled, non-loading XHR state: demo data is present the moment it renders. */
const DEMO_XHR_UI_STATUS: XhrStatusUIState = {
  isSaving: false,
  isLoading: false,
  hasInitialized: true,
  hasErrors: false,
  errors: [],
  lastFetch: undefined,
};

const getDemoFalseFlag = createSelector([getIsDemoManagerPresetActive], isActive =>
  isActive ? false : undefined
);

const getDemoEmptyIds = createSelector([getIsDemoManagerPresetActive], isActive =>
  isActive ? EMPTY_IDS : undefined
);

const getDemoXhrUiStatus = createSelector([getIsDemoManagerPresetActive], isActive =>
  isActive ? DEMO_XHR_UI_STATUS : undefined
);

// ─── Dashboard tab ────────────────────────────────────────────────────────────

/**
 * The whole dashboard fixture, built from the same week range the real dashboard
 * selectors derive from the filter, so the demo rows and graph line up with the
 * period the admin has selected.
 */
const getDemoDashboardData = createSelector(
  [getIsDemoManagerPresetActive, readDashboardFilter, getStartOfWeek],
  (isActive, filter, startOfWeek) => {
    if (!isActive) {
      return undefined;
    }
    const range = getLastWeeksRange(filter.to, filter.period, startOfWeek);
    return buildPeopleDemoData(range.since, filter.period);
  }
);

export const getDemoDashboardRows = createSelector(
  [getDemoDashboardData],
  (data): ItemStatusWrapper<UserViewModel>[] | undefined => data?.usersLoaded
);

export const getDemoGraphData = createSelector(
  [getDemoDashboardData],
  (data): HoursPerWeekData[] | undefined => data?.graphData
);

export const getDemoFilteredUserCapacityMap = createSelector(
  [getDemoDashboardData],
  (data): Record<number, number> | undefined => data?.userCapacityMap
);

export const getDemoFilteredUserChangedMap = createSelector(
  [getDemoDashboardData],
  (data): Record<number, { hasChanged: boolean }> | undefined => data?.userCapacityChangedMap
);

const readUserWeekDetailsModal = (state: IState) => state.usersPage.dashboard.userWeekDetailsModal;

/**
 * Clicking a week bar on the Dashboard opens this modal with whichever id and
 * week key the bar carried — for a demo person, that id was never in the real
 * `state.users.entries` the real selector reads, so it must resolve from the
 * same demo dashboard data the bar itself was rendered from, or the click
 * throws instead of opening anything. Always resolves once the id is a demo
 * id (a zero week rather than undefined for an unmatched key), so this never
 * falls through to the real selector, which wouldn't recognise the id either.
 */
export const getDemoUserWeekDetailsModalUi = createSelector(
  [getIsDemoManagerPresetActive, readUserWeekDetailsModal, getDemoDashboardRows],
  (isActive, modal, rows): { user: EnrichedUser; week: WeekSummary } | undefined => {
    if (!isActive || !modal || !DEMO_DETAIL_USER_IDS.has(modal.userId)) {
      return undefined;
    }
    const week = rows
      ?.find(row => row.item?.id === modal.userId)
      ?.item?.summary?.byWeek.find(w => w.week === modal.weekKey) ?? {
      week: modal.weekKey,
      capacity: durationFromHours(0),
      duration: durationFromHours(0),
      billable_duration: durationFromHours(0),
      non_billable_duration: durationFromHours(0),
      total_duration: durationFromHours(0),
      planned_duration: durationFromHours(0),
      tasked_duration: durationFromHours(0),
    };
    return { user: buildDemoEnrichedUser(modal.userId), week };
  }
);

export const getDemoDashboardLoadedCount = createSelector([getDemoDashboardRows], rows =>
  rows === undefined ? undefined : rows.length
);

export const getDemoDashboardUserIdsToLoad = getDemoEmptyIds;
export const getDemoDashboardHasMore = getDemoFalseFlag;
export const getDemoDashGraphXhrUiStatus = getDemoXhrUiStatus;
export const getDemoDashListXhrUiStatus = getDemoXhrUiStatus;

// ─── All Users tab ────────────────────────────────────────────────────────────

/**
 * The demo directory has no deleted people, so the Deleted tab reads empty
 * while still rendering its normal "no users" state. The Invited tab has its
 * own small set of fictional pending invites, built separately from the
 * active roster.
 */
export const getDemoListRows = createSelector(
  [getIsDemoManagerPresetActive, readListQuery],
  (isActive, query): ListUser[] | undefined => {
    if (!isActive) {
      return undefined;
    }
    if (query.show === 'deleted') {
      return [];
    }
    if (query.show === 'invited') {
      return buildInvitedListDemoUsers();
    }
    return buildListDemoUsers(formatDayRange(query.from, query.to).map(day => day.date));
  }
);

const getDemoListTotalsData = createSelector([getIsDemoManagerPresetActive], isActive =>
  isActive ? buildListDemoTotals() : undefined
);

export const getDemoListTotals = createSelector(
  [getDemoListTotalsData],
  (data): DurationSummary | undefined => data?.totals
);

export const getDemoSummedUserCapacity = createSelector([getDemoListTotalsData], data =>
  data === undefined ? undefined : data.totalCapacity
);

export const getDemoListLoadedSummaryCount = createSelector([getDemoListRows], rows =>
  rows === undefined ? undefined : rows.length
);

export const getDemoUserCount = createSelector([getIsDemoManagerPresetActive], isActive =>
  isActive ? DEMO_PEOPLE_COUNT : undefined
);

export const getDemoInvitedUserCount = createSelector([getIsDemoManagerPresetActive], isActive =>
  isActive ? DEMO_INVITED_PEOPLE_COUNT : undefined
);

/** Active + invited, for the People page's "All Users (N)" header — which,
 *  like the real account it swaps for, counts everyone regardless of tab. */
export const getDemoTotalUserCount = createSelector(
  [getDemoUserCount, getDemoInvitedUserCount],
  (activeCount, invitedCount) =>
    activeCount === undefined ? undefined : activeCount + (invitedCount ?? 0)
);

export const getDemoListUserIdsToLoad = getDemoEmptyIds;
export const getDemoIsFetchingUsers = getDemoFalseFlag;
export const getDemoIsLoadingUsers = getDemoFalseFlag;

// ─── Activities tab ───────────────────────────────────────────────────────────

export const getDemoActivities = createSelector(
  [getIsDemoManagerPresetActive],
  (isActive): Activity[] | undefined => (isActive ? buildActivitiesDemoData() : undefined)
);

export const getDemoActivityXhrStatus = getDemoXhrUiStatus;
export const getDemoActivityMaybeHasMore = getDemoFalseFlag;

// ─── Compliance tab ───────────────────────────────────────────────────────────

export const getDemoComplianceMetrics = createSelector(
  [getIsDemoManagerPresetActive],
  (isActive): ComplianceUserMetric[] | undefined =>
    isActive ? buildComplianceDemoUsers() : undefined
);

// ─── Single-person detail pages ───────────────────────────────────────────────

/**
 * The id of the demo person whose detail page is open, or `undefined` for every
 * other route — including a real person's detail page while the preset is on,
 * which keeps working against real data.
 */
const getDemoDetailUserId = createSelector(
  [getIsDemoManagerPresetActive, getCurrentlyViewedUserId],
  (isActive, userId): number | undefined =>
    isActive && (DEMO_DETAIL_USER_IDS.has(userId) || DEMO_INVITED_USER_IDS.has(userId))
      ? userId
      : undefined
);

/** The one check the single-person routes share: am I looking at a demo person? */
export const getIsViewingDemoUser = createSelector(
  [getDemoDetailUserId],
  (userId): boolean => userId !== undefined
);

export const getDemoSelectedUser = createSelector(
  [getDemoDetailUserId],
  (userId): EnrichedUser | undefined =>
    userId === undefined ? undefined : buildDemoEnrichedUser(userId)
);

export const getDemoTeamsForUser = createSelector(
  [getDemoDetailUserId],
  (userId): Team[] | undefined => (userId === undefined ? undefined : buildDemoTeamsForUser(userId))
);

/**
 * All 5 fictional teams with resolved members, for the People page's
 * "Manage teams" modal.
 */
export const getDemoTeamsWithUsers = createSelector(
  [getIsDemoManagerPresetActive],
  (isActive): TeamWithUsers[] | undefined => (isActive ? buildTeamsWithUsers() : undefined)
);

/**
 * Feeds the "Manage teams" modal (`components/teams/TeamsModal.tsx`), which
 * is only ever mounted from the People route — swapping here, rather than
 * in the shared `redux/selectors/teams.ts`, keeps every other consumer of
 * `getTeamsWithUsers` (Calendar's team picker, project team assignment,
 * etc.) reading real teams even while this preset is on.
 */
export const getTeamsWithUsersForManageTeamsModal = createSelector(
  [getDemoTeamsWithUsers, getTeamsWithUsers],
  (demo, real) => demo ?? real
);

const getDemoUserDashboardData = createSelector([getDemoDetailUserId], userId =>
  userId === undefined ? undefined : buildDetailDashboardTotals(userId)
);

export const getDemoUserDashboardSummary = createSelector(
  [getDemoUserDashboardData],
  (data): CostAndDurationSummary | undefined => data?.summary
);

export const getDemoUserDashboardCapacity = createSelector(
  [getDemoUserDashboardData],
  (data): number | undefined => data?.capacityHours
);

export const getDemoUserTimesheetRows = createSelector(
  [getDemoDetailUserId],
  (userId): DemoTimesheetRow[] | undefined =>
    userId === undefined ? undefined : buildDetailTimesheetRows(userId)
);

/**
 * The demo activity feed narrowed to the person whose detail page is open. Demo
 * people who never log anything simply have none, which reads as an empty feed
 * exactly like a quiet real person's does.
 */
export const getDemoUserActivities = createSelector(
  [getDemoDetailUserId, getDemoActivities],
  (userId, activities): Activity[] | undefined =>
    userId === undefined ? undefined : (activities ?? []).filter(a => a.user.id === userId)
);

/** A demo person's feed is complete on arrival: there is no next page to load. */
export const getDemoUserActivityMaybeHasMore = createSelector(
  [getDemoDetailUserId],
  (userId): boolean | undefined => (userId === undefined ? undefined : false)
);

export const getDemoUserReport = createSelector([getDemoDetailUserId], userId => {
  if (userId === undefined) {
    return undefined;
  }
  return {
    ...buildDetailReportTotals(userId),
    chart: buildDetailReportChart(userId),
    projects: buildDetailReportProjects(userId),
    tags: buildDetailReportTags(userId),
  };
});
